import React from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { firestore } from '../firebase/firebase';
import Tooltip from './Tooltip';
import SheetMusic from '../interfaces/sheetmusic';

interface AdminFeaturedListProps {
  featuredMusic: SheetMusic[];
  onReorder: (newOrder: SheetMusic[]) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  onAddDivider?: (title: string) => void;
}

const AdminFeaturedList: React.FC<AdminFeaturedListProps> = ({ featuredMusic, onReorder, onDragStart, onDragEnd, onAddDivider }) => {
  const [showDividerForm, setShowDividerForm] = React.useState(false);
  const [dividerTitle, setDividerTitle] = React.useState('');
  const handleDragStart = () => {
    if (onDragStart) onDragStart();
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) {
      if (onDragEnd) onDragEnd();
      return;
    }

    const items = Array.from(featuredMusic);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update the order in the items array with consecutive numbers
    const updatedItems = items.map((item, index) => ({
      ...item,
      featuredOrder: index
    }));

    console.log('Updating featured order:', updatedItems.map(item => ({ id: item.id, title: item.title, featuredOrder: item.featuredOrder })));

    // Update the local state immediately for better UX
    onReorder(updatedItems);

    // Update the database - make sure to wait for completion
    try {
      const batch = firestore.batch();
      updatedItems.forEach((item, index) => {
        const docRef = firestore.collection('music').doc(item.id);
        batch.update(docRef, { featuredOrder: index });
      });
      await batch.commit();
      console.log('Database update completed successfully');
    } catch (error) {
      console.error('Error updating featured order:', error);
      // Revert local state on error
      onReorder(featuredMusic);
    } finally {
      // Only allow Firebase updates after database operation completes
      if (onDragEnd) onDragEnd();
    }
  };

  const handleRemoveFromFeatured = async (sheet: SheetMusic) => {
    try {
      // First remove the item from featured
      const docRef = firestore.collection('music').doc(sheet.id);
      await docRef.update({ 
        featured: false,
        featuredOrder: null 
      });

      // Then reorder the remaining featured items
      const remainingItems = featuredMusic.filter(item => item.id !== sheet.id);
      if (remainingItems.length > 0) {
        const batch = firestore.batch();
        remainingItems.forEach((item, index) => {
          const itemRef = firestore.collection('music').doc(item.id);
          batch.update(itemRef, { featuredOrder: index });
        });
        await batch.commit();
      }
    } catch (error) {
      console.error('Error removing from featured:', error);
    }
  };

  const handleAddDivider = () => {
    if (dividerTitle.trim() && onAddDivider) {
      onAddDivider(dividerTitle.trim());
      setDividerTitle('');
      setShowDividerForm(false);
    }
  };

  return (
    <div>
      {/* Add Divider Button */}
      <div className="mb-4">
        {!showDividerForm ? (
            <button
              onClick={() => setShowDividerForm(true)}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-zinc-700 bg-zinc-100 border border-zinc-300 rounded-lg hover:bg-zinc-200 transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-2">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Pievienot jaunu sadaļu
            </button>
        ) : (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={dividerTitle}
              onChange={(e) => setDividerTitle(e.target.value)}
              placeholder="Iedalījuma nosaukums"
              className="flex-1 px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-zinc-500 focus:border-zinc-500 "
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddDivider();
                if (e.key === 'Escape') {
                  setShowDividerForm(false);
                  setDividerTitle('');
                }
              }}
              autoFocus
            />
            <button
              onClick={handleAddDivider}
              disabled={!dividerTitle.trim()}
              className="px-3 py-2 text-sm font-medium text-white bg-zinc-700 rounded-lg hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Pievienot
            </button>
            <button
              onClick={() => {
                setShowDividerForm(false);
                setDividerTitle('');
              }}
              className="px-3 py-2 text-sm font-medium text-zinc-700 bg-zinc-200 rounded-lg hover:bg-zinc-300 transition-colors"
            >
              Atcelt
            </button>
          </div>
        )}
      </div>

      {/* Drag and Drop List */}
    <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <Droppable droppableId="featured-music">
        {(provided, snapshot) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            className={`space-y-2 ${snapshot.isDraggingOver ? 'bg-zinc-50' : ''}`}
          >
            {featuredMusic.map((sheet, index) => (
              <Draggable key={sheet.id} draggableId={sheet.id} index={index}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    className={`group relative ${snapshot.isDragging ? 'z-50' : ''}`}
                  >
                    <div className={`flex items-center w-full rounded-lg transition-colors duration-200 ${
                    sheet.isDivider 
                      ? 'bg-zinc-800 hover:bg-zinc-700 p-2 min-h-[48px]' 
                      : 'bg-white border border-zinc-200 hover:bg-zinc-50 hover:border-zinc-300 p-4 min-h-[64px]'
                  }`}>
                      {/* Drag handle */}
                      <div
                        {...provided.dragHandleProps}
                        className={`mr-3 cursor-grab active:cursor-grabbing ${
                          sheet.isDivider 
                            ? 'text-zinc-400 hover:text-zinc-300' 
                            : 'text-zinc-400 hover:text-zinc-600'
                        }`}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 6h.01M8 12h.01M8 18h.01M16 6h.01M16 12h.01M16 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      
                      {/* Order number */}
                      <div className={`mr-3 text-sm font-mono min-w-[20px] ${
                        sheet.isDivider 
                          ? 'text-zinc-400' 
                          : 'text-zinc-500'
                      }`}>
                        {index + 1}.
                      </div>
                      
                      {/* Divider or Music title */}
                      {sheet.isDivider ? (
                        <div className="flex-grow">
                          <div className="mt-1 text-xs text-zinc-400 uppercase tracking-wide">
                            Sadaļa
                          </div>
                          <span className="text-white  font-bold text-base">
                            {sheet.title}
                          </span>
                        </div>
                      ) : (
                        <div 
                          className="flex-grow cursor-pointer"
                          onClick={() => window.open(sheet.downloadURL, '_blank', 'noopener,noreferrer')}
                        >
                          <span className="text-zinc-800  font-medium">
                            <strong>{sheet.title}</strong>, <span className="text-zinc-600">{sheet.author}</span>
                          </span>
                        </div>
                      )}
                      
                      {/* Remove button */}
                      <Tooltip content={sheet.isDivider ? "Dzēst iedalījumu" : "Noņemt no aktuālajām notīm"} position="top">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFromFeatured(sheet);
                          }}
                          className={`ml-3 p-2 rounded-lg transition-colors opacity-0 group-hover:opacity-100 ${
                            sheet.isDivider 
                              ? 'text-red-400 hover:text-red-300 hover:bg-red-900/20' 
                              : 'text-red-500 hover:text-red-700 hover:bg-red-50'
                          }`}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </Tooltip>
                    </div>
                    
                    {/* Drop indicator */}
                    {snapshot.isDragging && (
                      <div className="absolute inset-0 bg-zinc-200 border-2 border-dashed border-zinc-400 rounded-lg opacity-50"></div>
                    )}
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
    </div>
  );
};

export default AdminFeaturedList;